/**
 * Advanced CPR + Pivot Strategy — Dashboard Overlay
 *
 * Top-right "CPR MARKET ANALYSIS" table (§22), rendered from the rows
 * produced by the indicator engine. Sections start at the first row that
 * carries a `section` title.
 *
 * Also computes the §15 S/R confluence rows when the Advanced S/R Zones
 * indicator is active on the same panel (proximity vs nearest R/S).
 *
 * Responsive (desktop / tablet / mobile): compact type scale, internal
 * scroll for tall content, collapse toggle.
 * CSS prefix: cprdash-
 */
import React from "react";
import { CprDashboard, CprRow } from "../../indicators/built-in/advanced-cpr-pivot";
import type { AsrzDashboard } from "../../indicators/built-in/advanced-support-resistance-zones";
import "./CprStrategyOverlay.scss";

interface CprStrategyOverlayProps {
  dashboard: CprDashboard | null;
  priceScaleWidth: number;
  visible: boolean;
  showDashboard: boolean;
  onToggleDashboard: () => void;
  /** Advanced S/R Zones dashboard when that indicator is active (§15) */
  asrz: AsrzDashboard | null;
}

const fmtLvl = (v: number): string =>
  v.toLocaleString("en-IN", { maximumFractionDigits: 2 });

const CprStrategyOverlay: React.FC<CprStrategyOverlayProps> = ({
  dashboard, priceScaleWidth, visible, showDashboard, onToggleDashboard, asrz,
}) => {
  if (!visible) return null;

  const d = dashboard;
  const right = (priceScaleWidth > 0 ? priceScaleWidth : 62) + 14;

  // ── §15: confluence vs the platform's S/R engine (if active) ──
  const srRows: CprRow[] = [];
  if (d && asrz) {
    const near = (level: number | null, target: { price: number } | null): boolean => {
      if (level == null || !target || !isFinite(level) || level === 0) return false;
      return (Math.abs(level - target.price) / level) * 100 <= d.srProximityPct;
    };
    const resConf =
      near(d.cprTop, asrz.nearestAbove) || near(d.levels.r1, asrz.nearestAbove);
    const supConf =
      near(d.cprBottom, asrz.nearestBelow) || near(d.levels.s1, asrz.nearestBelow);
    if (resConf) {
      srRows.push({
        k: "S/R Confluence",
        v: "STRONG RESISTANCE CONFLUENCE",
        c: "warn",
        section: "S/R Confluence",
      });
    }
    if (supConf) {
      srRows.push({
        k: "S/R Confluence",
        v: "STRONG SUPPORT CONFLUENCE",
        c: "warn",
        section: resConf ? undefined : "S/R Confluence",
      });
    }
  }

  const allRows = d ? [...d.rows, ...srRows] : [];

  return (
    <div className="cprdash-dash" style={{ right }}>
      {showDashboard && d ? (
        <>
          <div className="cprdash-head">
            <span className="cprdash-title">{d.title}</span>
            <button className="cprdash-toggle" onClick={onToggleDashboard} title="Collapse">▲</button>
          </div>
          <div className="cprdash-body">
            {allRows.map((r, i) => (
              <React.Fragment key={`${i}-${r.k}`}>
                {r.section && <div className="cprdash-section">{r.section}</div>}
                <div className="cprdash-row">
                  <span className="cprdash-label" title={r.k}>{r.k}</span>
                  <span className={`cprdash-value cprdash-c--${r.c}`} title={r.v}>{r.v}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </>
      ) : (
        <div className="cprdash-head">
          <span className="cprdash-title">CPR Market Analysis</span>
          <button className="cprdash-toggle cprdash-toggle--right" onClick={onToggleDashboard} title="Expand">▼</button>
        </div>
      )}
    </div>
  );
};

export default CprStrategyOverlay;
